import { useState } from "react";

import { useEntityAuditLogs } from "../../../../hooks/queries/admin/auditLogs";
import { useDebounce } from "../../../../hooks/useDebounce";
import { AUDIT_LOG_CONDITION_TABLE } from "../../../../services/admin/auditLogService";
import {
  AuditLogPanelFilters,
  DEFAULT_PANEL_FILTERS,
  IAuditLogPanelFilterState,
} from "./AuditLogPanelFilters";
import { AuditLogTable } from "./AuditLogTable";
import {
  auditWindowError,
  auditWindowToRange,
  CHANGE_OPERATIONS,
  IAuditLogWindow,
  latestAuditWindow,
} from "./auditLogWindow";

/** Rows per page before anyone touches the selector — the /admin default. */
const DEFAULT_PAGE_SIZE = 10;

export interface IAuditLogPanelProps {
  /** Audited table, e.g. `cms_user`, `cms_hospital`, `cms_condition`. */
  tableName: string;
  /** Primary key of the audited row. Falsy keeps the query idle. */
  entityId: string | number | null | undefined;
  title?: string;
  pageSize?: number;
}

/** The operations to ask for: the chosen one, else every change when views are hidden. */
function operationsFor(filters: IAuditLogPanelFilterState) {
  if (filters.operation) return [filters.operation];
  return filters.hideViews ? CHANGE_OPERATIONS : undefined;
}

/**
 * Embeddable audit trail for a single entity — the portal port of Retool's
 * `moduleAuditLogs` global widget (EN-5147). Retool embedded that module in
 * Patients, Archived Patients, Hospital Details, Staff Details and Wards;
 * the portal does the same with this one component, so those pages only
 * supply a table name and an entity id:
 *
 *   <AuditLogPanel tableName="cms_user" entityId={patient.userId} />
 *
 * This is the entity-scoped data wrapper (EN-5288): one server-side page of
 * one window of at most 90 days, filtered by action, source and user, with
 * views hidden until asked for. The table, computed "Action" column and row
 * drill-down live in `AuditLogTable`, which EN-5164's survey answer history
 * reuses against a different endpoint.
 */
export function AuditLogPanel({
  tableName,
  entityId,
  title = "Audit log",
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
}: IAuditLogPanelProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [auditWindow, setAuditWindow] =
    useState<IAuditLogWindow>(latestAuditWindow);
  const [filters, setFilters] = useState(DEFAULT_PANEL_FILTERS);
  // Typing an email re-queries once the user pauses, not on every key.
  const applicationUserEmail = useDebounce(
    filters.applicationUserEmail.trim(),
    400
  );

  const { auditLogs, pagination, isLoading, isError, error } =
    useEntityAuditLogs(
      {
        tableName,
        entityId: entityId != null ? String(entityId) : "",
        ...auditWindowToRange(auditWindow),
        operations: operationsFor(filters),
        source: filters.source || undefined,
        applicationUserEmail: applicationUserEmail || undefined,
        page,
        pageSize,
      },
      // An invalid window is explained by the filter bar, not sent.
      !auditWindowError(auditWindow)
    );

  return (
    <AuditLogTable
      logs={auditLogs}
      pagination={pagination}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPage(1);
      }}
      isLoading={isLoading}
      isError={isError}
      error={error}
      title={title}
      filters={
        <AuditLogPanelFilters
          idPrefix={`audit-${tableName}`}
          auditWindow={auditWindow}
          onWindowChange={(next) => {
            setAuditWindow(next);
            setPage(1);
          }}
          filters={filters}
          onFiltersChange={(next) => {
            setFilters(next);
            setPage(1);
          }}
        />
      }
      refreshKeys={[["admin-entity-audit-logs"]]}
      // Condition rows come from the per-patient endpoint, which keeps the snapshots.
      loadDetails={tableName !== AUDIT_LOG_CONDITION_TABLE}
    />
  );
}
